import * as z from 'zod'

import {
  bracketCeilingSchema,
  compareBracketCeilings,
} from '#dictionary/bracketCeiling'
import {
  compareFilingStatuses,
  filingStatusSchema,
} from '#dictionary/filingStatuses'
import { compareRegions, regionSchema } from '#dictionary/regions'

import stateBracketsRaw from './taxStateBrackets.json'

// Per-state marginal-tax bracket schedules. Zero-tax states (AK, FL, NH,
// NV, SD, TN, TX, WA, WY) are represented as a single
// `{ to: null, taxRate: 0 }` row per (year, filingStatus) — consumer code
// doesn't need special-casing.
//
// See ./README.md for the full data-quality report from the Airtable
// migration and the known outstanding issues tracked in ZL-1027.
export const taxStateBracketSchema = z
  .object({
    year: z.int(),
    region: regionSchema,
    filingStatus: filingStatusSchema,
    to: bracketCeilingSchema,
    taxRate: z.number().min(0).max(1),
  })
  .strict()

export type TaxStateBracket = z.infer<typeof taxStateBracketSchema>

// Sort at parse time by (year, region, filingStatus, to) ASC. The bracket-
// cascade contract requires strictly-ascending `to` within a
// (year, region, filingStatus) group — guarded by invariant tests.
const parseAndSort = (raw: unknown): readonly TaxStateBracket[] =>
  z
    .array(taxStateBracketSchema)
    .parse(raw)
    .sort(
      (a, b) =>
        a.year - b.year ||
        compareRegions(a.region, b.region) ||
        compareFilingStatuses(a.filingStatus, b.filingStatus) ||
        compareBracketCeilings(a.to, b.to)
    )

export const STATE_BRACKETS = parseAndSort(stateBracketsRaw)
